import { useState, useEffect } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Trash2, Loader2, Users, Star, FileText, Share2, X, UserPlus, Download, Scale, MoreHorizontal, ArrowRight, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Clients = () => {
  const { token, isCliente, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vigenciaFilter, setVigenciaFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  // Create/Edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientStatus, setClientStatus] = useState('ACTIVO');
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Access/Share modal state - ÉPICA 3.2.1
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [accessClient, setAccessClient] = useState(null);
  const [clientAccess, setClientAccess] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [shareAccessLevel, setShareAccessLevel] = useState('VIEWER');
  const [sharingLoading, setSharingLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [page, limit, statusFilter, vigenciaFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const skip = (page - 1) * limit;
      const promises = [
        axios.get(`${API}/clients`, {
          params: {
            search,
            skip,
            limit,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            vigencia: vigenciaFilter === 'expiring' ? 'expiring' : undefined
          },
          headers: { Authorization: `Bearer ${token}` }
        }),
        !isCliente() ? axios.get(`${API}/favorites/clients`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ];

      // Fetch users for sharing (Admin only)
      if (isAdmin()) {
        promises.push(axios.get(`${API}/organization/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })));
      }

      const results = await Promise.all(promises);
      const clientsData = results[0]?.data;
      if (clientsData?.items) {
        setClients(clientsData.items);
        setTotalCount(clientsData.total);
      } else {
        setClients(Array.isArray(clientsData) ? clientsData : []);
        setTotalCount(Array.isArray(clientsData) ? clientsData.length : 0);
      }

      const favData = results[1]?.data || [];
      setFavoriteIds(new Set(favData.map(f => f.client_id)));

      if (isAdmin() && results[2]) {
        setUsers(results[2]?.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setPage(1);
      fetchData();
    }
  };

  const toggleFavorite = async (clientId) => {
    const isFavorite = favoriteIds.has(clientId);

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/clients/${clientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavoriteIds(prev => {
          const next = new Set(prev);
          next.delete(clientId);
          return next;
        });
        toast.success('Removido de favoritos');
      } else {
        await axios.post(`${API}/favorites/clients/${clientId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavoriteIds(prev => new Set([...prev, clientId]));
        toast.success('Agregado a favoritos');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Error al actualizar favoritos');
    }
  };

  // ===== ACCESS MODAL FUNCTIONS - ÉPICA 3.2.1 =====
  const handleOpenAccessModal = async (client) => {
    setAccessClient(client);
    setAccessModalOpen(true);
    setAccessLoading(true);
    setSelectedUserIds(new Set());

    try {
      const res = await axios.get(`${API}/clients/${client.id}/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientAccess(res.data);
    } catch (error) {
      console.error('Error fetching access:', error);
      toast.error('Error al cargar accesos');
    } finally {
      setAccessLoading(false);
    }
  };

  const handleRemoveAccess = async (userId) => {
    try {
      await axios.delete(`${API}/clients/${accessClient.id}/access/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientAccess(prev => prev.filter(a => a.user_id !== userId));
      toast.success('Acceso removido');
    } catch (error) {
      console.error('Error removing access:', error);
      toast.error('Error al remover acceso');
    }
  };

  const handleShareAccess = async () => {
    if (selectedUserIds.size === 0) {
      toast.error('Seleccione al menos un usuario');
      return;
    }

    setSharingLoading(true);
    try {
      const res = await axios.post(`${API}/clients/${accessClient.id}/access/share`, {
        user_ids: Array.from(selectedUserIds),
        access_level: shareAccessLevel
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { added, already_had, errors } = res.data;

      if (added.length > 0) {
        toast.success(`${added.length} usuario(s) agregado(s)`);
      }
      if (already_had.length > 0) {
        toast.info(`${already_had.length} ya tenían acceso`);
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} error(es)`);
      }

      // Refresh access list
      const accessRes = await axios.get(`${API}/clients/${accessClient.id}/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientAccess(accessRes.data);
      setSelectedUserIds(new Set());
    } catch (error) {
      console.error('Error sharing access:', error);
      toast.error('Error al compartir acceso');
    } finally {
      setSharingLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const getAvailableUsers = () => {
    const existingUserIds = new Set(clientAccess.map(a => a.user_id));
    return users.filter(u => !existingUserIds.has(u.id) && u.role !== 'ADMIN_DESPACHO');
  };

  // ===== CRUD FUNCTIONS =====
  const handleOpenCreateModal = () => {
    setEditingClient(null);
    setEditingClient(null);
    setClientName('');
    setClientStatus('ACTIVO');
    setModalOpen(true);
  };

  const handleOpenEditModal = (client) => {
    setEditingClient(client);
    setClientName(client.nombre);
    // If backend had status, we would set it here. Mocking for now as requested if not available.
    setClientStatus('ACTIVO');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingClient(null);
    setClientName('');
    setClientStatus('ACTIVO');
  };

  const handleSave = async () => {
    const trimmedName = clientName.trim();
    if (!trimmedName) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, {
          nombre: trimmedName
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cliente actualizado');
      } else {
        await axios.post(`${API}/clients`, {
          nombre: trimmedName,
          status: clientStatus
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cliente creado');
      }

      handleCloseModal();
      await fetchData();
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error(editingClient ? 'Error al actualizar cliente' : 'Error al crear cliente');
    } finally {
      setSaving(false);
    }
  };

  // Export clients - ÉPICA 3.3
  const handleExportClients = async () => {
    setExporting(true);
    try {
      const params = {
        search,
        limit: 10000,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        vigencia: vigenciaFilter === 'expiring' ? 'expiring' : undefined
      };

      const res = await axios.get(`${API}/clients`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      const itemsToExport = res.data?.items || res.data || [];

      const headers = ['Nombre', 'Status', 'Email', 'Teléfono', 'RFC', 'Marcas', 'Procedimientos', 'Alertas', 'Creado'];
      const rows = itemsToExport.map(client => [
        client.nombre || '',
        client.status || 'ACTIVO',
        client.email || '',
        client.telefono || '',
        client.rfc || '',
        client.marcas_count || 0,
        client.procedimientos_count || 0,
        client.alertas_count || 0,
        client.created_at ? new Date(client.created_at).toLocaleDateString('es-MX') : ''
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `clientes_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Exportación completada');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Error al exportar clientes');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteClick = (client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;

    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/clients/${clientToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Cliente desactivado', {
        description: clientToDelete.nombre
      });

      await fetchData();
    } catch (error) {
      console.error('Error deleting client:', error);

      // P0 HOTFIX: Manejo específico de errores
      if (error.response?.status === 409) {
        toast.error('No se puede eliminar', {
          description: error.response.data.detail || 'El cliente tiene marcas activas',
          duration: 5000
        });
      } else if (error.response?.status === 403) {
        toast.error('Solo admin puede eliminar clientes');
      } else {
        toast.error(error.response?.data?.detail || 'Error al desactivar cliente');
      }
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  };

  const activeClients = clients.filter(c => c.activo !== false);

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'nombre', direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedClients = [...clients].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key] || '';
    let bValue = b[sortConfig.key] || '';

    // Handle nested or complex fields if needed (e.g. date)
    if (sortConfig.key === 'created_at') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };


  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Clientes</h2>
            <p className="text-sm text-gray-500 mt-1">Administración de clientes del despacho</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Export button - ÉPICA 3.3 */}
            {totalCount > 0 && (
              <Button
                variant="outline"
                onClick={handleExportClients}
                className="border-gray-300"
                data-testid="export-clients-btn"
                disabled={exporting}
              >
                {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Exportar ({totalCount})
              </Button>
            )}
            {!isCliente() && (
              <Button
                onClick={handleOpenCreateModal}
                className="bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="new-client-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Nuevo Cliente
              </Button>
            )}
          </div>
        </div>

        {/* Table/Card View Container */}
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex-1 min-w-[240px] relative">
              <Input
                placeholder="Buscar por nombre, email o RFC..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearch}
                className="pl-9"
              />
              <Users className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Status:</span>
                <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setPage(1); }}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="ACTIVO">Activo</SelectItem>
                    <SelectItem value="INACTIVO">Inactivo</SelectItem>
                    <SelectItem value="PRIORITARIO">Prioritario</SelectItem>
                    <SelectItem value="PROSPECTO">Prospecto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Vigencia:</span>
                <Select value={vigenciaFilter} onValueChange={(val) => { setVigenciaFilter(val); setPage(1); }}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Cualquiera</SelectItem>
                    <SelectItem value="expiring">Con alertas vigentes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Mostrar:</span>
                <Select value={String(limit)} onValueChange={(val) => { setLimit(Number(val)); setPage(1); }}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center border rounded-lg overflow-hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-none border-r"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <div className="px-3 py-1 text-sm bg-gray-50 font-medium">
                  {page} / {Math.ceil(totalCount / limit) || 1}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-none border-l"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= Math.ceil(totalCount / limit)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            </div>
          ) : clients.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No se encontraron clientes</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('nombre')}>
                        Nombre {getSortIcon('nombre')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marcas</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Procedimientos</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alertas</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('created_at')}>
                        Creado {getSortIcon('created_at')}
                      </th>
                      {!isCliente() && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {sortedClients.map((client) => (
                      <tr
                        key={client.id}
                        className="group hover:bg-gray-50/80 transition-colors cursor-pointer"
                        onClick={() => navigate(`/clients/${client.id}`)}
                        data-testid={`client-row-${client.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium group-hover:bg-white group-hover:shadow-sm transition-all border border-transparent group-hover:border-gray-200">
                              {client.nombre ? client.nombre.charAt(0).toUpperCase() : '?'}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">{client.nombre || 'Sin nombre'}</span>
                                {favoriteIds.has(client.id) && (
                                  <Star className="h-4 w-4 text-indigo-400 fill-indigo-400" />
                                )}
                              </div>
                              {client.email && (
                                <div className="text-xs text-gray-500">{client.email}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ACTIVO
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); navigate(`/clients/${client.id}`); }}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8"
                            data-testid={`view-trademarks-${client.id}`}
                          >
                            <FileText className="h-4 w-4 mr-1.5" />
                            {client.marcas_count !== undefined ? client.marcas_count : 'Ver'}
                          </Button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); navigate(`/clients/${client.id}?tab=procedimientos`); }}
                            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 h-8 font-medium"
                          >
                            <Scale className="h-4 w-4 mr-1.5" />
                            {client.procedimientos_count !== undefined ? client.procedimientos_count : 'Ver'}
                          </Button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); navigate(`/clients/${client.id}?tab=alertas`); }}
                            className={`h-8 font-medium ${client.alertas_count > 0
                              ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                              }`}
                          >
                            <Bell className="h-4 w-4 mr-1.5" />
                            {client.alertas_count !== undefined ? client.alertas_count : '—'}
                          </Button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {client.created_at ? new Date(client.created_at).toLocaleDateString('es-MX', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }) : 'N/A'}
                        </td>
                        {!isCliente() && (
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-1">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <span className="sr-only">Abrir menú</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)}>
                                    <FileText className="mr-2 h-4 w-4" />
                                    Ver Marcas
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}?tab=procedimientos`)}>
                                    <Scale className="mr-2 h-4 w-4" />
                                    Ver Procedimientos
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => navigate(`/procedures?create=true&client_id=${client.id}`)}>
                                    <Plus className="mr-2 h-4 w-4" />
                                    Nuevo Procedimiento
                                  </DropdownMenuItem>

                                  <DropdownMenuSeparator />

                                  <DropdownMenuItem onClick={() => toggleFavorite(client.id)}>
                                    <Star className={`mr-2 h-4 w-4 ${favoriteIds.has(client.id) ? 'fill-indigo-500 text-indigo-500' : ''}`} />
                                    {favoriteIds.has(client.id) ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                                  </DropdownMenuItem>

                                  {isAdmin() && (
                                    <>
                                      <DropdownMenuItem onClick={() => handleOpenAccessModal(client)}>
                                        <Share2 className="mr-2 h-4 w-4" />
                                        Compartir Acceso
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleOpenEditModal(client)}>
                                        <Pencil className="mr-2 h-4 w-4" />
                                        Editar Nombre
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(client); }}
                                        className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                      >
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Eliminar Cliente
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {sortedClients.map((client) => (
                  <div
                    key={client.id}
                    className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm cursor-pointer"
                    onClick={() => navigate(`/clients/${client.id}`)}
                    data-testid={`client-card-${client.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium">
                          {client.nombre ? client.nombre.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-base font-medium text-gray-900">{client.nombre || 'Sin nombre'}</span>
                            {favoriteIds.has(client.id) && (
                              <Star className="h-4 w-4 text-indigo-400 fill-indigo-400" />
                            )}
                          </div>
                          {client.email && (
                            <div className="text-xs text-gray-500">{client.email}</div>
                          )}
                        </div>
                      </div>
                      {!isCliente() && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <span className="sr-only">Abrir menú</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)}>
                              <FileText className="mr-2 h-4 w-4" />
                              Ver Marcas
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}?tab=procedimientos`)}>
                              <Scale className="mr-2 h-4 w-4" />
                              Ver Procedimientos
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/procedures?create=true&client_id=${client.id}`)}>
                              <Plus className="mr-2 h-4 w-4" />
                              Nuevo Procedimiento
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />

                            <DropdownMenuItem onClick={() => toggleFavorite(client.id)}>
                              <Star className={`mr-2 h-4 w-4 ${favoriteIds.has(client.id) ? 'fill-indigo-500 text-indigo-500' : ''}`} />
                              {favoriteIds.has(client.id) ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                            </DropdownMenuItem>

                            {isAdmin() && (
                              <>
                                <DropdownMenuItem onClick={() => handleOpenAccessModal(client)}>
                                  <Share2 className="mr-2 h-4 w-4" />
                                  Compartir Acceso
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleOpenEditModal(client)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Editar Nombre
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); handleDeleteClick(client); }}
                                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Eliminar Cliente
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                      <div>
                        <p className="font-medium">Estado:</p>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ACTIVO
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">Creado:</p>
                        <p>{client.created_at ? new Date(client.created_at).toLocaleDateString('es-MX', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : 'N/A'}</p>
                      </div>
                      <div className="col-span-2 flex gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/clients/${client.id}`); }}
                          className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 font-semibold"
                        >
                          <FileText className="h-4 w-4 mr-1.5" />
                          {client.marcas_count !== undefined ? `${client.marcas_count} Marcas` : 'Ver Marcas'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/clients/${client.id}?tab=procedimientos`); }}
                          className="flex-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 h-8 font-medium"
                        >
                          <Scale className="h-4 w-4 mr-1.5" />
                          {client.procedimientos_count !== undefined ? `${client.procedimientos_count} Proc.` : 'Proc.'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/clients/${client.id}?tab=alertas`); }}
                          className={`flex-1 h-8 font-medium ${client.alertas_count > 0
                            ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-200'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                          <Bell className="h-4 w-4 mr-1.5" />
                          {client.alertas_count !== undefined ? client.alertas_count : '0'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
            <DialogDescription>
              {editingClient ? 'Modifica los datos del cliente.' : 'Ingresa el nombre del nuevo cliente.'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="c-name">Nombre del cliente</Label>
                <Input
                  id="c-name"
                  placeholder="Ej. Empresa SA de CV"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !saving && handleSave()}
                  disabled={saving}
                  autoFocus
                  data-testid="client-name-input"
                />
              </div>

              {!editingClient && (
                <div className="space-y-2">
                  <Label htmlFor="c-status">Estatus inicial</Label>
                  <Select value={clientStatus} onValueChange={setClientStatus} disabled={saving}>
                    <SelectTrigger id="c-status">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVO">Activo</SelectItem>
                      <SelectItem value="INACTIVO">Inactivo</SelectItem>
                      <SelectItem value="PRIORITARIO">Prioritario</SelectItem>
                      <SelectItem value="PROSPECTO">Prospecto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCloseModal} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving || !clientName.trim()} className="bg-gray-900 hover:bg-gray-800" data-testid="save-client-button">
              {saving ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Guardando...</>) : (editingClient ? 'Guardar' : 'Crear')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access/Share Modal - ÉPICA 3.2.1 */}
      <Dialog open={accessModalOpen} onOpenChange={setAccessModalOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Accesos - {accessClient?.nombre}
            </DialogTitle>
            <DialogDescription>
              Gestiona quién puede ver las marcas de este cliente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Current access list */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Usuarios con acceso</Label>
              {accessLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : clientAccess.length === 0 ? (
                <p className="text-sm text-gray-500 py-2">Ningún usuario tiene acceso asignado.</p>
              ) : (
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {clientAccess.map(access => (
                    <div key={access.id} className="px-3 py-2 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{access.user_nombre}</p>
                        <p className="text-xs text-gray-500">{access.user_email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${access.access_level === 'EDITOR' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                          {access.access_level === 'EDITOR' ? 'Editor' : 'Lector'}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAccess(access.user_id)}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                          data-testid={`remove-access-${access.user_id}`}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add new users */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-2 block">Compartir con usuarios</Label>
              {getAvailableUsers().length === 0 ? (
                <p className="text-sm text-gray-500">No hay más usuarios disponibles.</p>
              ) : (
                <>
                  <div className="border rounded-lg max-h-40 overflow-y-auto mb-3">
                    {getAvailableUsers().map(user => (
                      <div
                        key={user.id}
                        className="px-3 py-2 flex items-center gap-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleUserSelection(user.id)}
                      >
                        <Checkbox
                          checked={selectedUserIds.has(user.id)}
                          onCheckedChange={() => toggleUserSelection(user.id)}
                        />
                        <div className="flex-1">
                          <p className="text-sm">{user.nombre}</p>
                          <p className="text-xs text-gray-500">{user.email} • {user.role === 'OPERADOR' ? 'Operador' : user.role === 'CLIENTE' ? 'Cliente' : user.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-3">
                    <Select value={shareAccessLevel} onValueChange={setShareAccessLevel}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="VIEWER">Lector</SelectItem>
                        <SelectItem value="EDITOR">Editor</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      onClick={handleShareAccess}
                      disabled={sharingLoading || selectedUserIds.size === 0}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="share-access-button"
                    >
                      {sharingLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4 mr-1" />
                          Compartir ({selectedUserIds.size})
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessModalOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar este cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              El cliente <span className="font-semibold">{clientToDelete?.nombre}</span> será desactivado.
              Las marcas asociadas se mantendrán pero no aparecerá en la lista de clientes activos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Desactivando...</>) : 'Desactivar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
